import CONFIG from "../config";
import { getToken } from "../utils/auth";
import emailjs from "@emailjs/browser";

/**
 * OPTION 3: Client-side EmailJS SDK Integration (200 free emails/month)
 * 
 * Required Environment Variables in .env:
 * - REACT_APP_EMAILJS_PUBLIC_KEY
 * - REACT_APP_EMAILJS_SERVICE_ID
 * - REACT_APP_EMAILJS_TEMPLATE_ID
 * 
 * Get these from: https://www.emailjs.com/docs/rest-api/
 */

// Initialize EmailJS
let emailjsReady = false;
try {
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  if (publicKey) {
    emailjs.init(publicKey);
    emailjsReady = true;
    console.log('[EmailJS] ✅ Client-side SDK initialized');
  } else {
    console.log('[EmailJS] ⚠️  Public key missing (REACT_APP_EMAILJS_PUBLIC_KEY). Check .env');
  }
} catch (err) {
  console.error('[EmailJS] ❌ Initialization failed:', err.message);
}

/**
 * Send contract notification to buyer via email and WhatsApp
 * Priority: Server API (uses DB config) → Client EmailJS SDK (200 free/month)
 */
export const sendContractNotification = async ({
  buyerName,
  buyerEmail,
  buyerPhone,
  buyerWhatsapp,
  username,
  tempPassword,
  domainName,
  storeName,
  contractNumber,
}) => {
  try {
    const token = getToken();

    // Prepare notification payload
    const notificationPayload = {
      buyerName,
      buyerEmail,
      buyerPhone,
      buyerWhatsapp,
      username,
      tempPassword,
      domainName,
      storeName,
      contractNumber,
    };

    console.log('[Notification] Attempting to send contract details...', { buyerEmail, storeName });

    // PRIMARY: Try server API first (uses DB profile config + server-side setup)
    try {
      console.log('[Notification] Calling server API endpoint...');
      const response = await fetch(`${CONFIG.API_URL}/api/admin/notifications/send-contract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(notificationPayload),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(`Server API returned ${response.status}: ${result.message}`);
      }

      console.log('[Notification] ✅ Server sent successfully:', result);
      return result;
    } catch (serverErr) {
      console.warn('[Notification] ⚠️  Server API failed:', serverErr.message);
      
      // FALLBACK: Try client-side EmailJS SDK (200 free emails/month)
      if (emailjsReady) {
        try {
          console.log('[Notification] Falling back to client-side EmailJS...');
          const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
          const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
          
          if (!serviceId || !templateId) {
            throw new Error('EmailJS serviceId or templateId missing from .env (REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID)');
          }

          // Send via EmailJS with template variables
          const emailResult = await emailjs.send(serviceId, templateId, {
            to_email: buyerEmail,
            to_name: buyerName,
            username: username,
            temp_password: tempPassword,
            domain: domainName,
            store_name: storeName,
            contract_number: contractNumber,
          });

          console.log('[Notification] ✅ Email sent via client-side EmailJS:', emailResult);
          return {
            success: true,
            message: 'Email sent via client-side EmailJS SDK (200 free/month)',
            email: buyerEmail,
            emailjsResponse: emailResult,
          };
        } catch (emailJsErr) {
          console.error('[Notification] ❌ Client EmailJS failed:', emailJsErr);
          return {
            success: false,
            message: 'Both server and client email sending failed',
            serverError: serverErr.message,
            emailjsError: emailJsErr.message,
            helpText: 'Check .env for REACT_APP_EMAILJS_* keys, or ask admin to set EMAILJS_SERVICE_ID in .env (server-side)',
          };
        }
      } else {
        console.error('[Notification] ❌ Server failed and EmailJS SDK not ready');
        return {
          success: false,
          message: 'Email service unavailable. Check server logs or set up EmailJS credentials.',
          serverError: serverErr.message,
          helpText: 'Add REACT_APP_EMAILJS_PUBLIC_KEY, REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID to client .env',
        };
      }
    }
  } catch (error) {
    console.error("[Notification] Fatal error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Send password reset link
 */
export const sendPasswordReset = async (email) => {
  try {
    const response = await fetch(`${CONFIG.API_URL}/api/auth/send-reset-link`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending password reset:", error);
    throw error;
  }
};

/**
 * Send custom message via WhatsApp
 */
export const sendWhatsAppMessage = async (phoneNumber, message) => {
  try {
    const token = getToken();

    const response = await fetch(`${CONFIG.API_URL}/api/admin/notifications/whatsapp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ phoneNumber, message }),
    });

    if (!response.ok) {
      throw new Error(`WhatsApp API returned ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending WhatsApp message:", error);
    throw error;
  }
};

/**
 * Send email message
 */
export const sendEmail = async (to, subject, html) => {
  try {
    const token = getToken();

    const response = await fetch(`${CONFIG.API_URL}/api/admin/notifications/email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ to, subject, html }),
    });

    if (!response.ok) {
      throw new Error(`Email API returned ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending email:", error);
    throw error;
  }
};
